import React, { useState, useEffect, useRef, useCallback } from "react"
import Select from "react-select"
import { MdRefresh } from "react-icons/md"
import { CircularProgress } from "@mui/material"
import KpiCards from "./KpiCards"
import ChartGrid from "./ChartGrid"
import FrameworkTable from "./FrameworkTable"
import { DATASETS, COLORS, TABLE_FRAMEWORKS } from "./constants"

const BASE_API = (process.env.REACT_APP_BASE_API_URL || "").replace(/\/+$/, "")

const selectStyles = {
  control: (provided) => ({
    ...provided,
    borderColor: COLORS.cardBorder,
    backgroundColor: COLORS.background,
    boxShadow: "none",
    minHeight: "36px",
    fontSize: "12px",
    "&:hover": { borderColor: COLORS.primaryTeal },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "4px",
    color: COLORS.grayText,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (provided) => ({ ...provided, zIndex: 9999, fontSize: "12px" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused || state.isSelected ? COLORS.altRow2 : "white",
    color: state.isSelected ? COLORS.primaryTeal : COLORS.darkText,
    fontSize: "12px",
  }),
  placeholder: (provided) => ({ ...provided, fontSize: "12px", color: "#999" }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: COLORS.altRow2,
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({ ...provided, color: COLORS.primaryTeal, fontSize: "11px" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: COLORS.primaryTeal,
    ":hover": { backgroundColor: COLORS.primaryTeal, color: "#fff" },
  }),
}

const EMPTY_DASHBOARD = {
  kpis: { company_count: 0, average_csrd_page_count: 0, average_material_topics_count: 0, external_assurance_percent: 0, data_quality_score_avg: 0, net_zero_count: 0, net_zero_target_year: null },
  chartData: { emissions_profile: [], companies_by_country: [], workforce_and_board_diversity: [], eu_taxonomy_percent: 0, iro_visual_format: {}, water_waste_profile: [] },
  esrsData: [],
  alignmentData: [],
  warnings: [],
}

const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0

function buildDashboardFromCompanies(companies) {
  const count = companies.length
  const withDqScore = companies.filter((c) => c.data_quality_score != null)
  const avgDqScore = withDqScore.length > 0 ? avg(withDqScore.map((c) => c.data_quality_score)) * 100 : 0
  const netZeroCompanies = companies.filter((c) => c.net_zero_target_year != null)
  const netZeroCount = netZeroCompanies.length
  const renewablePcts = companies.map((c) => c.renewable_energy_pct).filter((v) => v != null)
  const frameworkCounts = companies.map((c) => (c.frameworks || []).filter((f) => TABLE_FRAMEWORKS.includes(f)).length).filter((v) => v > 0)

  const withEmissions = companies
    .filter((c) => c.scope1_emissions_tco2e != null || c.scope2_emissions_tco2e != null)
    .sort((a, b) =>
      ((b.scope1_emissions_tco2e || 0) + (b.scope2_emissions_tco2e || 0) + (b.scope3_emissions_tco2e || 0))
      - ((a.scope1_emissions_tco2e || 0) + (a.scope2_emissions_tco2e || 0) + (a.scope3_emissions_tco2e || 0)))
    .slice(0, 10)

  const countryCounts = {}
  companies.forEach((c) => {
    if (c.country) countryCounts[c.country] = (countryCounts[c.country] || 0) + 1
  })

  const withDiversity = companies
    .filter((c) => c.board_diversity_female_pct != null || c.female_workforce_pct != null)
    .slice(0, 10)

  const withWaterWaste = companies
    .filter((c) => c.water_consumption_m3 != null || c.waste_generated_tonnes != null)
    .sort((a, b) => (b.water_consumption_m3 || 0) - (a.water_consumption_m3 || 0))
    .slice(0, 10)

  return {
    kpis: {
      company_count: count,
      average_csrd_page_count: renewablePcts.length > 0 ? avg(renewablePcts) : 0,
      average_material_topics_count: frameworkCounts.length > 0 ? avg(frameworkCounts) : 0,
      external_assurance_percent: count > 0 ? (withDqScore.filter((c) => c.data_quality_score > 0.1).length / count) * 100 : 0,
      data_quality_score_avg: avgDqScore,
      net_zero_count: netZeroCount,
      net_zero_target_year: netZeroCompanies.length === 1 ? netZeroCompanies[0].net_zero_target_year : null,
    },
    chartData: {
      emissions_profile: withEmissions.map((c) => ({
        company: c.company_name,
        scope1: c.scope1_emissions_tco2e ?? null,
        scope2: c.scope2_emissions_tco2e ?? null,
        scope3: c.scope3_emissions_tco2e ?? null,
      })),
      companies_by_country: Object.entries(countryCounts)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 15),
      workforce_and_board_diversity: withDiversity.map((c) => ({
        company: c.company_name,
        board_female_pct: c.board_diversity_female_pct ?? null,
        workforce_female_pct: c.female_workforce_pct ?? null,
      })),
      water_waste_profile: withWaterWaste.map((c) => ({
        company: c.company_name,
        water_m3: c.water_consumption_m3 ?? null,
        waste_tonnes: c.waste_generated_tonnes ?? null,
      })),
      eu_taxonomy_percent: 0,
      iro_visual_format: {},
    },
    esrsData: companies
      .filter((c) => c.esrs_standards && c.esrs_standards.length > 0)
      .slice(0, 20)
      .map((c) => ({ company: c.company_name, esrs: c.esrs_standards.join(", ") })),
    alignmentData: companies.slice(0, 50).map((c) => ({
      company: c.company_name,
      frameworks: c.frameworks || [],
      gri_alignment: c.frameworks?.includes("GRI") || false,
      brsr_alignment: c.frameworks?.includes("BRSR") || false,
      sbti_target_mentioned: c.frameworks?.includes("SBTi") || false,
      tcfd_referenced: c.frameworks?.includes("TCFD") || false,
      sbti_committed: c.sbti_committed === true || c.sbti_committed === "true" || c.sbti_committed === 1,
      net_zero_target_year: c.net_zero_target_year ?? null,
      esg_rating: c.esg_rating ?? null,
      report_url: c.sustainability_report_url ?? c.report_url ?? null,
    })),
    warnings: [],
  }
}

const DashboardPanel = ({ dataset = DATASETS.CSRD, onCompanyChange, onYearChange }) => {
  const [loading, setLoading] = useState(true)
  const [filterOptions, setFilterOptions] = useState({ companies: [], sectors: [], countries: [], years: [] })
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedCompetitors, setSelectedCompetitors] = useState([])
  const [selectedYear, setSelectedYear] = useState(null)
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD)

  const abortRef = useRef(null)

  // Reset filters when dataset changes
  useEffect(() => {
    setSelectedCompany(null)
    setSelectedCompetitors([])
    setSelectedYear(null)
  }, [dataset])

  // Fetch filter options when dataset changes
  useEffect(() => {
    const ctrl = new AbortController()
    ;(async () => {
      try {
        const res = await fetch(
          `${BASE_API}/csrd-benchmark-filter-options?dataset=${dataset}`,
          { headers: { "Content-Type": "application/json" }, signal: ctrl.signal }
        )
        if (!res.ok) return
        const data = await res.json()
        const rawCompanies = data.companies || data.companies_metadata || []
        const rawSectors = data.sectors || []
        const rawCountries = data.countries || []
        const rawYears = data.years || []

        const yearOptions = rawYears
          .filter((y) => { const n = Number(typeof y === "number" ? y : (y.value ?? y)); return Number.isInteger(n) && isFinite(n) })
          .map((y) => typeof y === "number" ? { label: String(y), value: y } : { label: String(y.label), value: y.value })
          .sort((a, b) => b.value - a.value)

        setFilterOptions({
          companies: rawCompanies.map((c) => ({ label: c.company_name || c.name || c, value: c.company_name || c.name || c })),
          sectors: rawSectors.map((s) => typeof s === "string" ? { label: s, value: s } : { label: s.label, value: s.value }),
          countries: rawCountries.map((c) => typeof c === "string" ? { label: c, value: c } : { label: c.label, value: c.value }),
          years: yearOptions,
        })
        if (yearOptions.length > 0) {
          setSelectedYear((prev) => prev ?? yearOptions[0].value)
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Failed to fetch filter options:", err)
      }
    })()
    return () => ctrl.abort()
  }, [dataset])

  // Fetch dashboard data when filters or dataset change
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    ;(async () => {
      try {
        if (dataset === DATASETS.CSRD) {
          await fetchCsrdData(ctrl.signal)
        } else {
          await fetchBenchmarkData(ctrl.signal)
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Failed to fetch dashboard data:", err)
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    })()

    return () => ctrl.abort()
  }, [selectedCompany, selectedCompetitors, selectedYear, dataset])

  useEffect(() => { if (onCompanyChange) onCompanyChange(selectedCompany) }, [selectedCompany])
  useEffect(() => { if (onYearChange) onYearChange(selectedYear) }, [selectedYear])

  const fetchCsrdData = async (signal) => {
    const hasFilters = selectedCompany || (selectedCompetitors && selectedCompetitors.length > 0)
    const body = hasFilters
      ? JSON.stringify({
          primary_company: selectedCompany || selectedCompetitors[0]?.value,
          competitors: selectedCompetitors.map((c) => c.value).filter((v) => v !== selectedCompany),
          year: selectedYear || 2024,
        })
      : ""

    const res = await fetch(`${BASE_API}/csrd-benchmark-dashboard-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      signal,
    })

    if (!res.ok) return
    const data = await res.json()

    setDashboard({
      kpis: {
        company_count: data.kpis?.company_count || 0,
        average_csrd_page_count: data.kpis?.average_csrd_page_count || 0,
        average_material_topics_count: data.kpis?.average_material_topics_count || 0,
        external_assurance_percent: data.charts?.external_assurance_percent || 0,
        data_quality_score_avg: data.kpis?.data_quality_score_avg ?? 0,
        net_zero_count: data.kpis?.net_zero_count ?? 0,
        net_zero_target_year: data.kpis?.net_zero_target_year ?? null,
      },
      chartData: {
        emissions_profile: data.charts?.emissions_profile || [],
        companies_by_country: data.charts?.companies_by_country || [],
        workforce_and_board_diversity: data.charts?.workforce_and_board_diversity || [],
        eu_taxonomy_percent: data.charts?.eu_taxonomy_percent || 0,
        iro_visual_format: data.charts?.iro_visual_format || {},
        water_waste_profile: data.charts?.water_waste_profile || [],
      },
      esrsData: data.tables?.esrs_standards_mentioned || [],
      alignmentData: data.tables?.framework_alignment || [],
      warnings: data.warnings || [],
    })
  }

  const fetchBenchmarkData = async (signal) => {
    const res = await fetch(`${BASE_API}/csrd-benchmark-companies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dataset, sector: null, skip: 0, limit: 200 }),
      signal,
    })

    if (!res.ok) return
    const data = await res.json()
    let companies = data.companies || []

    if (selectedCompany || (selectedCompetitors && selectedCompetitors.length > 0)) {
      const filterNames = new Set()
      if (selectedCompany) filterNames.add(selectedCompany)
      selectedCompetitors.forEach((c) => filterNames.add(c.value))
      companies = companies.filter((c) => filterNames.has(c.company_name))
    }

    setDashboard(buildDashboardFromCompanies(companies))
  }

  const handleReset = () => {
    setSelectedCompany(null)
    setSelectedCompetitors([])
    setSelectedYear(null)
  }

  const { kpis, chartData, esrsData, alignmentData, warnings } = dashboard

  return (
    <div style={{ height: "100%", overflowY: "auto", backgroundColor: "#FAFAFA", padding: "16px 20px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 0.8fr auto", gap: "10px", marginBottom: "16px", alignItems: "end" }}>
        <div>
          <label style={{ fontSize: "11px", color: COLORS.grayText, fontWeight: "500", marginBottom: "4px", display: "block" }}>
            Company
          </label>
          <Select
            value={selectedCompany ? filterOptions.companies.find((o) => o.value === selectedCompany) : null}
            options={filterOptions.companies}
            onChange={(opt) => setSelectedCompany(opt ? opt.value : null)}
            isClearable
            placeholder="All companies"
            styles={selectStyles}
          />
        </div>
        <div>
          <label style={{ fontSize: "11px", color: COLORS.grayText, fontWeight: "500", marginBottom: "4px", display: "block" }}>
            Competitors (up to 5)
          </label>
          <Select
            value={selectedCompetitors}
            options={filterOptions.companies.filter((o) => o.value !== selectedCompany)}
            onChange={(opts) => { if ((opts || []).length <= 5) setSelectedCompetitors(opts || []) }}
            isMulti
            placeholder="Select competitors"
            styles={selectStyles}
          />
        </div>
        <div>
          <label style={{ fontSize: "11px", color: COLORS.grayText, fontWeight: "500", marginBottom: "4px", display: "block" }}>
            Year
          </label>
          <Select
            value={selectedYear ? filterOptions.years.find((o) => o.value === selectedYear) : null}
            options={filterOptions.years}
            onChange={(opt) => setSelectedYear(opt ? opt.value : null)}
            isClearable
            placeholder="Select year"
            styles={selectStyles}
          />
        </div>
        <button
          onClick={handleReset}
          style={{
            display: "flex", alignItems: "center", gap: "4px", padding: "8px 14px",
            border: `1px solid ${COLORS.primaryTeal}`, borderRadius: "6px",
            backgroundColor: "transparent", color: COLORS.primaryTeal,
            fontSize: "12px", cursor: "pointer", height: "36px", whiteSpace: "nowrap",
          }}
        >
          <MdRefresh size={14} /> Reset
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <CircularProgress style={{ color: COLORS.primaryTeal }} />
        </div>
      ) : (
        <>
          {warnings.length > 0 && (selectedCompany || selectedCompetitors.length > 0) && (
            <div style={{ backgroundColor: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", fontSize: "11px", color: "#8D6E00" }}>
              <strong>Data Quality Notes:</strong>
              <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px" }}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <KpiCards kpis={kpis} dataset={dataset} />
          <ChartGrid chartData={chartData} dataset={dataset} />
          <FrameworkTable esrsData={esrsData} alignmentData={alignmentData} dataset={dataset} />
        </>
      )}
    </div>
  )
}

export default DashboardPanel
